/**
 * Parse a stored ``PlaygroundSchedule`` (or "no schedule yet") into the
 * shape that ``scheduleFormSchema`` expects as default values.
 *
 * Backend exposes ``day_of_week`` / ``day_of_month`` as derived fields on
 * the response (parsed from ``cron_expr``); we prefer those when present
 * and fall back to ``cron_expr`` for older API responses or legacy
 * unsupported crons. Standard crons run through ``cronstrue`` for
 * validation; anything it rejects falls back to the form defaults.
 */
import cronstrue from "cronstrue";
import { PlaygroundSchedule } from "./types";
import type { ScheduleFormValues } from "./useScheduleForm";

// The dialog form excludes "custom"; legacy crons that don't fit
// daily / weekly / monthly fall back to "daily" so the picker stays usable.
type FormFrequency = "daily" | "weekly" | "monthly";

interface CronFormFields {
  frequency: FormFrequency;
  hour: string;
  dayOfWeek: string;
  dayOfMonth: string;
}

const FORM_DEFAULTS: CronFormFields = {
  frequency: "daily",
  // Mirrors backend defaults — Monday weekly, day 1 monthly.
  hour: "9",
  dayOfWeek: "1",
  dayOfMonth: "1",
};

/**
 * Extract the structured cron fields the form needs. Expressions go through
 * `cronstrue`, and anything it rejects falls back to the form defaults.
 */
const parseCronExpr = (cronExpr: string): CronFormFields => {
  const parts = cronExpr.split(" ");
  if (parts.length < 5) return FORM_DEFAULTS;

  const hourPart = parts[1];
  const hour = hourPart !== "*" ? String(parseInt(hourPart, 10)) : FORM_DEFAULTS.hour;

  try {
    cronstrue.toString(cronExpr, { throwExceptionOnParseError: true });
  } catch {
    return FORM_DEFAULTS;
  }

  if (parts[4] !== "*") {
    return { ...FORM_DEFAULTS, frequency: "weekly", hour, dayOfWeek: parts[4] };
  }
  if (parts[2] !== "*") {
    // `cronstrue` accepts non-numeric day-of-month tokens (e.g. the retired
    // `L` last-day-of-month sentinel), so reaching here does not guarantee a
    // numeric day. The picker only offers days 1-31; fall back to defaults
    // for anything else rather than feeding the form a junk `dayOfMonth`
    // that later serialises to `NaN`.
    const dayNum = parseInt(parts[2], 10);
    if (!isNaN(dayNum) && dayNum >= 1 && dayNum <= 31) {
      return { ...FORM_DEFAULTS, frequency: "monthly", hour, dayOfMonth: String(dayNum) };
    }
    return { ...FORM_DEFAULTS, hour };
  }
  return { ...FORM_DEFAULTS, hour };
};

export const parseCronToFormValues = (
  schedule: PlaygroundSchedule | undefined,
  sessionName: string
): ScheduleFormValues => {
  if (!schedule) {
    return {
      name: `${sessionName.slice(0, 247)} Report`,
      contentScope: "first_query",
      ...FORM_DEFAULTS,
      slackWebhookId: "",
      emails: "",
    };
  }

  const fromCron = schedule.cron_expr ? parseCronExpr(schedule.cron_expr) : FORM_DEFAULTS;

  // Prefer the backend's derived day_of_week / day_of_month — they're
  // already validated and skip the cron-parse branch for legacy crons.
  const dayOfWeek =
    schedule.day_of_week !== undefined && schedule.day_of_week !== null
      ? String(schedule.day_of_week)
      : fromCron.dayOfWeek;
  const dayOfMonth =
    schedule.day_of_month !== undefined && schedule.day_of_month !== null
      ? String(schedule.day_of_month)
      : fromCron.dayOfMonth;

  return {
    name: schedule.name,
    contentScope: schedule.content_scope,
    frequency: fromCron.frequency,
    hour: fromCron.hour,
    dayOfWeek,
    dayOfMonth,
    slackWebhookId: schedule.slack_webhook_id ? String(schedule.slack_webhook_id) : "",
    emails: schedule.emails?.join(", ") || "",
  };
};
